import { useState } from 'react';
import { Button } from '../ui/button';
import { ArrowUp, ArrowDown, Save, Loader2, Palette } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const SECTION_LABELS = {
  about: 'نبذة عن المبادرة',
  features: 'المميزات',
  testimonials: 'آراء المشاركين',
  videos: 'فيديوهات',
  gallery: 'معرض الصور',
};

const PRESET_COLORS = [
  { label: 'افتراضي', value: '' },
  { label: 'أبيض', value: '#ffffff' },
  { label: 'كريمي', value: '#faf6f1' },
  { label: 'رمادي فاتح', value: '#f3f4f6' },
  { label: 'بيج', value: '#f5f0e8' },
  { label: 'أخضر فاتح', value: '#f0fdf4' },
  { label: 'أزرق فاتح', value: '#eff6ff' },
];

const SectionOrderEditor = ({ sections }) => {
  const orderable = ['about', 'features', 'testimonials', 'videos', 'gallery'];

  const initOrder = () => {
    const items = orderable.map((key, idx) => ({
      key,
      order: sections[key]?.order ?? idx,
      bg_color: sections[key]?.bg_color || '',
    }));
    items.sort((a, b) => a.order - b.order);
    return items;
  };

  const [items, setItems] = useState(initOrder);
  const [saving, setSaving] = useState(false);
  const [showPicker, setShowPicker] = useState(null);

  const move = (idx, dir) => {
    const next = idx + dir;
    if (next < 0 || next >= items.length) return;
    const copy = [...items];
    [copy[idx], copy[next]] = [copy[next], copy[idx]];
    setItems(copy);
  };

  const setColor = (idx, color) => {
    const copy = [...items];
    copy[idx] = { ...copy[idx], bg_color: color };
    setItems(copy);
    setShowPicker(null);
  };

  const save = async () => {
    setSaving(true);
    try {
      const orders = {};
      items.forEach((item, idx) => {
        orders[item.key] = { order: idx, bg_color: item.bg_color };
      });
      await api.put('/admin/landing-order', { orders });
      toast.success('تم حفظ الترتيب والألوان بنجاح');
    } catch {
      toast.error('فشل حفظ الترتيب');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="section-order-editor">
      <p className="text-sm text-muted-foreground mb-4">
        تحكم في ترتيب الأقسام على صفحة الهبوط واختر لون خلفية كل قسم
      </p>

      <div className="space-y-2">
        {items.map((item, idx) => (
          <div
            key={item.key}
            className="flex items-center gap-3 p-4 rounded-xl border bg-white transition-all hover:shadow-sm"
            data-testid={`order-item-${item.key}`}
          >
            <span className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center font-bold text-sm">
              {idx + 1}
            </span>

            <span className="flex-1 font-medium text-accent">{SECTION_LABELS[item.key]}</span>

            {/* Color indicator & picker */}
            <div className="relative">
              <button
                onClick={() => setShowPicker(showPicker === idx ? null : idx)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs hover:bg-gray-50 transition-colors"
                data-testid={`color-btn-${item.key}`}
              >
                <div
                  className="w-5 h-5 rounded border"
                  style={{ backgroundColor: item.bg_color || '#faf6f1' }}
                />
                <Palette className="w-3.5 h-3.5 text-gray-500" />
              </button>

              {showPicker === idx && (
                <div className="absolute left-0 top-full mt-2 bg-white rounded-xl shadow-xl border p-3 z-20 w-52">
                  <div className="grid grid-cols-4 gap-2 mb-3">
                    {PRESET_COLORS.map(c => (
                      <button
                        key={c.value}
                        onClick={() => setColor(idx, c.value)}
                        className={`w-10 h-10 rounded-lg border-2 transition-all hover:scale-110 ${item.bg_color === c.value ? 'border-primary ring-2 ring-primary/30' : 'border-gray-200'}`}
                        style={{ backgroundColor: c.value || '#faf6f1' }}
                        title={c.label}
                      />
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-gray-500">مخصص:</label>
                    <input
                      type="color"
                      value={item.bg_color || '#faf6f1'}
                      onChange={e => setColor(idx, e.target.value)}
                      className="w-8 h-8 rounded cursor-pointer border-0"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Arrow buttons */}
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="icon"
                className="w-8 h-8"
                disabled={idx === 0}
                onClick={() => move(idx, -1)}
                data-testid={`move-up-${item.key}`}
              >
                <ArrowUp className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="w-8 h-8"
                disabled={idx === items.length - 1}
                onClick={() => move(idx, 1)}
                data-testid={`move-down-${item.key}`}
              >
                <ArrowDown className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Button onClick={save} disabled={saving} className="bg-primary hover:bg-primary/90 mt-4" data-testid="save-order-btn">
        {saving ? <Loader2 className="w-4 h-4 animate-spin ml-1" /> : <Save className="w-4 h-4 ml-1" />}
        حفظ الترتيب والألوان
      </Button>
    </div>
  );
};

export default SectionOrderEditor;
